import "./MonthlyReports.css";
import { Checkbox, Tags } from "../../../ui";
import AppFileNode from "../../components/AppFileNode/AppFileNode";
import ReportFilesManager from "../../components/ReportFilesManager/ReportFilesManager";
import { fileNameOf, isVersionedReport } from "../../components/ReportFileSlot/ReportFileSlot";
import {
  APPS,
  APP_STATUS,
  MONTH_NAMES,
  MONTHLY_SPEC,
} from "../../utils/ludDocuments.constants";
import {
  formatDate,
  getLudFileName,
  openLudFile,
} from "../../utils/ludDocuments.helpers";

export default function MonthlyReports({
  restaurant,
  lang,
  token,
  refreshRestaurant,
  canManage,
  onError,
}) {
  const ar = lang === "ar";
  const isL = restaurant.plan === "L";
  const periodOptions = [
    { value: "orders", label: ar ? "طلبات" : "Orders" },
    { value: "compensations", label: ar ? "تعويضات" : "Compensations" },
    { value: "monthlyReport", label: ar ? "تقرير شهري" : "Monthly report" },
    { value: "taxInvoice", label: ar ? "الضريبة" : "Tax" },
  ];

  return (
    <>
      <div className="lud-report-headline">
        {canManage&&restaurant.enabledApps.map((key) => {
          const appStatus = APP_STATUS[restaurant.appStatus?.[key] || "active"];
          const missing = restaurant.monthlyMissing.includes(key);
          const label = !appStatus.needsFiles
            ? appStatus.label[lang]
            : missing
              ? ar
                ? "ناقص"
                : "Missing"
              : ar
                ? "مكتمل"
                : "Complete";
          const variant = !appStatus.needsFiles
            ? appStatus.variant
            : missing
              ? "warning"
              : "success";

          return (
            <button
              key={key}
              type="button"
              className="lud-report-headline__chip"
              onClick={() =>
                document
                  .getElementById(`app-${key}`)
                  ?.scrollIntoView({ behavior: "smooth", block: "center" })
              }
            >
              <span>{APPS[key].short[lang]}</span>
              <strong>{APPS[key].label[lang]}</strong>
              <Tags variant={variant}>{label}</Tags>
            </button>
          );
        })}
      </div>

      <ReportFilesManager
        restaurant={restaurant}
        canManage={canManage}
        onError={onError}
        lang={lang}
        token={token}
        refreshRestaurant={refreshRestaurant}
        reportType="monthly"
        title={ar ? "رفع التقارير الشهرية" : "Upload monthly reports"}
        periodOptions={periodOptions}
        renderReports={({
          reportsByApp,
          renderFileSlot,
          deleteMode,
          selectedIds,
          toggleSelected,
        }) => (
          <>
            {restaurant.enabledApps.map((key) => {
              const missing = restaurant.monthlyMissing.includes(key);
              const coverage = restaurant.monthlyCoverage[key];
              const files = MONTHLY_SPEC[key] || [];
              const appReports = (reportsByApp[key] || [])
                .slice()
                .sort(
                  (a, b) =>
                    Number(b.year) - Number(a.year) ||
                    Number(b.month) - Number(a.month),
                );

              return (
                <AppFileNode
                  key={key}
                  id={`app-${key}`}
                  title={APPS[key].label[lang]}
                  subtitle={
                    coverage
                      ? `${coverage.monthsCount} ${ar ? "أشهر مرفوعة — من" : "months uploaded — from"} ${coverage.from} ${ar ? "إلى" : "to"} ${coverage.to}`
                      : ar
                        ? "لا أشهر مرفوعة بعد"
                        : "No months uploaded yet"
                  }
                  status={
                    missing
                      ? ar
                        ? "متأخر عن يوم 9"
                        : "Delayed after day 9"
                      : ar
                        ? "محدّث"
                        : "Updated"
                  }
                  statusVariant={missing ? "warning" : "success"}
                >
                  {appReports.length > 0
                    ? Object.entries(
                        appReports.reduce((groups, report) => {
                          const groupKey = `${report.year}-${report.month}`;
                          groups[groupKey] = [
                            ...(groups[groupKey] || []),
                            report,
                          ];
                          return groups;
                        }, {}),
                      ).map(([groupKey, groupReports]) => {
                        const [year, month] = groupKey.split("-").map(Number);
                        return (
                          <div key={groupKey}>
                            <div className="lud-report-tree-title">
                              {MONTH_NAMES[lang][month - 1]} {year} —{" "}
                              {files.length}{" "}
                              {ar
                                ? "عنصر متوقع — آخر شهر مكتمل"
                                : "expected items — latest completed month"}
                            </div>
                            {groupReports.map((report) => {
                              const periodLabel =
                                periodOptions.find(
                                  (option) => option.value === report.period,
                                )?.label || report.period;
                              return (
                                <div
                                  className="lud-report-file lud-report-file--actual"
                                  key={report.id}
                                >
                                  {deleteMode && (
                                    <Checkbox
                                      checked={selectedIds.includes(report.id)}
                                      onChange={() => toggleSelected(report.id)}
                                      onClick={(event) =>
                                        event.stopPropagation()
                                      }
                                    />
                                  )}
                                  <div className="lud-report-file__content">
                                    <strong>{periodLabel}</strong>
                                    <button
                                      type="button"
                                      className="lud-file-name-link"
                                      onClick={() =>
                                        openLudFile(report.currentFile, {
                                          token,
                                          storeId: restaurant.id,
                                          kind: "report",
                                          reference: report.id,
                                          onError,
                                        })
                                      }
                                    >
                                      {isVersionedReport(report)
                                        ? fileNameOf(report)
                                        : report.currentFile
                                        ? getLudFileName({
                                            file: {
                                              ...report.currentFile,
                                              app: key,
                                              type: "monthly",
                                            },
                                            kind: "report",
                                            reference: report.id,
                                            originalName:
                                              report.currentFile.originalName,
                                          })
                                        : ar
                                          ? "يُرفع يدوياً"
                                          : "Uploaded manually"}
                                    </button>
                                    {renderFileSlot(report)}
                                  </div>
                                  <div className="lud-report-file__meta">
                                    <span>
                                      {report.currentFile?.uploadedAt
                                        ? `${ar ? "رُفع بتاريخ" : "Uploaded on"} ${formatDate(report.currentFile.uploadedAt, lang)}`
                                        : ar
                                          ? "مرفوع"
                                          : "Uploaded"}
                                    </span>
                                    <Tags variant="success">
                                      {ar ? "سارٍ" : "Valid"}
                                    </Tags>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })
                    : files.map((file) => {
                        const blockedByPlan = file.source === "daily" && isL;
                        const sourceLabel = blockedByPlan
                          ? ar
                            ? "يتطلب التقرير اليومي — غير متاح بباقة L"
                            : "Requires daily report — unavailable on Plan L"
                          : file.source === "daily"
                            ? ar
                              ? "مصدره التقرير اليومي"
                              : "Sourced from the daily report"
                            : ar
                              ? "يُرفع يدوياً"
                              : "Uploaded manually";

                        return (
                          <div className="lud-report-file" key={file.name.en}>
                            <div>
                              <strong>{file.name[lang]}</strong>
                              <small>
                                {sourceLabel}
                                {file.count
                                  ? ` — ${ar ? "المتوقع" : "expected"} ${file.count}`
                                  : ""}
                              </small>
                            </div>
                            <div className="lud-report-file__meta">
                              <span>
                                {blockedByPlan
                                  ? "—"
                                  : file.source === "daily"
                                    ? ar
                                      ? "مأخوذ تلقائياً"
                                      : "Taken automatically"
                                    : ar
                                      ? "قبل 3 أيام"
                                      : "3 days ago"}
                              </span>
                              <Tags
                                variant={blockedByPlan ? "danger" : "success"}
                              >
                                {blockedByPlan
                                  ? ar
                                    ? "يُرفع يدوياً بدلاً من ذلك"
                                    : "Upload manually instead"
                                  : ar
                                    ? "سارٍ"
                                    : "Valid"}
                              </Tags>
                            </div>
                          </div>
                        );
                      })}
                </AppFileNode>
              );
            })}
          </>
        )}
      />
    </>
  );
}
